import Fuse from 'fuse.js';
import { useMemo } from 'react';

export type SearchOptions<T> = Fuse.IFuseOptions<T>;

/**
 * Thin wrapper around `fuse.js`.
 *
 * @description this performs an shallow equality check with the `options` object
 * so make sure it's either declared outside the function component or memoized.
 *
 * @param data - data to perform the search on
 * @param {Fuse.IFuseOptions} options - fuse.js options
 * @param {string} text - Term to perform the search on
 * @returns {Array<Fuse.FuseResult>} an array of fuse.js results.
 */
export default function useSearch<T>(
  data: T[],
  options: SearchOptions<T>,
  text: string
) {
  const searcher = useMemo(() => {
    const defaultOptions = { tokenize: true, threshold: 0.2 };
    return new Fuse<T>(data, { ...defaultOptions, ...options });
  }, [data, options]);

  const result = useMemo(() => searcher.search(text), [searcher, text]);

  return result;
}
